import { isEmpty, isNil } from 'lodash'

import { apiService, fetchingAPI } from '@/api'
import { apiErrorMessage } from '@/api/handleResponse'
import { toast } from '@/components/toasts/toast.helpers'
import { withoutLegacyModel } from '@/reducers/prompts/prompts.helpers'

import {
  setLoading,
  setError,
  setRun,
  setChangedCalls,
  setCallsLoading,
  setCallsError,
  setPendingAdoption,
  recordDismissed,
  resetRun,
  REGRESSION_SCOPE,
} from './promptimizer.redux'

// How many eligible calls a run measures over, and the floor below which it refuses. Sent explicitly rather than
// left to the server's defaults (100 / 50) because these are a product choice about the button. The floor sits
// well under the target on purpose: at floor == target a single missing transcript fails the whole run.
export const SAMPLE_SIZE = 50
export const MIN_SAMPLE_SIZE = 20

// `fetchingAPI` returns either a parsed body or a Response depending on the call; every consumer normalises it the
// same way (see prompts.actions.js).
export const readBody = async (response) =>
  typeof response?.json === 'function' ? response.json() : response

/**
 * The run as the UI holds it — the API's snake_case renamed once, in one place, rather than at each of the four
 * call sites that read it.
 */
const toRun = (data) => ({
  optimizationId: data.optimization_id,
  criterionId: data.criterion_id,
  status: data.status,
  progress: {
    nSampled: data.progress?.n_sampled ?? 0,
    groundTruthStatus: data.progress?.ground_truth_status ?? null,
    nLabeled: data.progress?.n_labeled ?? 0,
    nCandidates: data.progress?.n_candidates ?? 0,
    sampling: {
      stage: data.progress?.sampling?.stage ?? null,
      nFilterMatched: data.progress?.sampling?.n_filter_matched ?? null,
      nChecked: data.progress?.sampling?.n_checked ?? null,
      nEligible: data.progress?.sampling?.n_eligible ?? null,
      nTarget: data.progress?.sampling?.n_target ?? null,
    },
  },
  rankBy: data.rank_by ?? null,
  incumbentMetrics: data.incumbent_metrics ?? null,
  candidates: data.candidates ?? [],
  winner: data.winner ?? null,
  costUsd: data.cost_usd ?? null,
  // The model both columns were measured on. Not necessarily the one the criterion runs today — see the result
  // panel, which says so when they differ.
  targetModel: data.target_model ?? null,
  targetEffort: data.target_effort ?? null,
  targetModelId: data.target_model_id ?? null,
  // Only ever set on a failed run — the server withholds it otherwise, so a stale reason can't sit beside a
  // healthy status.
  failureReason: data.failure_reason ?? null,
  // Why a finished run wrote no candidates, when that was deliberate. An empty candidate list otherwise reads as
  // "every version failed", which is a fault — the opposite of this.
  stoppedEarly: data.stopped_early ?? null,
  // Whether the scorecard's eligibility settings moved since this ran, so the panel can say the numbers are over
  // the calls it covered at the time. Null while a run is unfinished, and on runs that recorded no config.
  eligibilityChanged: data.eligibility_changed ?? null,
  // The prompt the run set out to improve, from the run's own row — the authority, since the click-time snapshot
  // only exists in memory and is gone after a reload, leaving the diff nothing to compare against. Omitted when
  // the server sends none, so it cannot overwrite that snapshot with null.
  ...(isNil(data.starting_prompt_text) ? {} : { originalPrompt: data.starting_prompt_text }),
})

// NO PRE-FLIGHT CALL COUNT, deliberately — `/bulk-score/preview` was tried here and removed.
//
// Counting the calls a scorecard reaches means ENUMERATING them — `get_calls_eligible_for_scorecard_config` runs a
// broad query, hydrates per-call context, then applies `check_filter_match` in Python per call. Over three months
// of a busy org that blows the 60-second statement timeout. The run itself is the cheap path: it passes its
// budget as the endpoint's `limit`, so the query pages and stops early. The sampler reports the real number as its
// first milestone anyway.

/**
 * Every run on this scorecard that is still worth showing, newest first.
 *
 * Returns the whole list rather than one run: each criterion picks its own out of it, and several criteria can have
 * one at the same time. The server stops returning a run once the scorecard is saved again.
 */
export const fetchLatestRun =
  ({ scorecardConfigId }) =>
  async (dispatch) => {
    try {
      const response = await fetchingAPI(
        `${apiService.summary}/promptimizer/optimize/pending?scorecard_config_id=${scorecardConfigId}`,
        'GET',
        dispatch
      )
      const data = await readBody(response)

      return (data?.runs ?? []).map((run) => ({
        optimizationId: run.optimization_id,
        status: run.status,
        criterionId: run.criterion_id,
        promptId: run.prompt_id,
        criterionDraft: run.criterion_draft,
        // Reported, not filtered out server-side — see the note there. The caller takes the newest run for the
        // criterion and shows nothing when it carries this, which keeps "latest only" the one rule.
        dismissed: run.dismissed === true,
      }))
    } catch (error) {
      // Silent: a convenience on mount. Failing it must not put an error on a criterion someone opened for
      // unrelated reasons.
      return []
    }
  }

/**
 * Save the prompt currently in the editor as its own `prompts` row, so a run can measure text that was never saved
 * as a criterion.
 *
 * The same call the scorecard save path already makes for every new prompt. It is also what keeps an unoptimized
 * prompt out of production: saving the scorecard mints an immediately-active config version, so requiring that
 * first would publish the un-optimized text for the whole run.
 */
export const savePromptForOptimization =
  ({ prompt, organizationId, criteriaScid }) =>
  async (dispatch) => {
    dispatch(setLoading({ criteriaScid, loading: { start: true } }))

    // Optimize runs outside Formik's submit, so the criteria form's model rule never sees this click. Without an
    // llm_model_id the API has no model to run the prompt on, so refuse here rather than POST a prompt it cannot
    // execute. The button is disabled for this too; this is the backstop for it.
    if (!prompt?.llm_model_id) {
      dispatch(setLoading({ criteriaScid, loading: { start: false } }))
      dispatch(
        setError({ criteriaScid, error: { kind: 'savePromptFailed', detail: 'no model selected' } })
      )
      toast.error('Select an AI model before optimizing this prompt')

      return null
    }

    try {
      const response = await fetchingAPI(
        `${apiService.summary}/prompts`,
        'POST',
        dispatch,
        JSON.stringify(withoutLegacyModel({ ...prompt, organization_id: organizationId }))
      )
      const data = await readBody(response)

      return data?.id ?? null
    } catch (error) {
      // MUST be caught: `fetchingAPI` throws on a non-2xx, and an uncaught rejection out of a click handler
      // renders nothing at all, which is indistinguishable from a dead button.
      dispatch(
        setError({ criteriaScid, error: { kind: 'savePromptFailed', detail: error?.message } })
      )
      // Toasted here rather than by a component reading `error`, which would see a render-time snapshot still
      // holding the previous value inside the click handler that just dispatched this.
      toast.error(
        isEmpty(error?.message)
          ? 'Could not save the prompt to optimize'
          : `Could not save the prompt to optimize: ${error.message}`
      )

      return null
    } finally {
      dispatch(setLoading({ criteriaScid, loading: { start: false } }))
    }
  }

export const startOptimization =
  ({
    criterionId = null,
    promptId = null,
    scorecardConfigId = null,
    criterionDraft = null,
    criteriaScid = null,
  }) =>
  async (dispatch) => {
    dispatch(setLoading({ criteriaScid, loading: { start: true } }))

    try {
      const response = await fetchingAPI(
        `${apiService.summary}/promptimizer/optimize`,
        'POST',
        dispatch,
        JSON.stringify({
          criterion_id: criterionId,
          prompt_id: promptId,
          scorecard_config_id: scorecardConfigId,
          n: SAMPLE_SIZE,
          min_sample: MIN_SAMPLE_SIZE,
          // The criterion form as it stands. Not a run input — nothing server-side reads it — but a run outlives
          // the page, so this is what lets a recovered run put the editor back the way it was.
          criterion_draft: criterionDraft,
        })
      )
      const data = await readBody(response)

      dispatch(
        setRun({
          criteriaScid,
          run: { optimizationId: data.optimization_id, criterionId, status: data.status },
        })
      )

      return data.optimization_id
    } catch (error) {
      // 409 is the server's single-flight rule — worth naming, because the fix is "look at the run you have", not
      // "try again".
      // ⚠ `fetchingAPI` does NOT reject uniformly: `handleResponse` rejects an Error carrying `status` for
      // 403/404/422/500/504, but for 400 and 409 it rejects the RAW PARSED BODY, with no `status` and the text on
      // `detail` or `message` depending on the service. So 409 cannot be recognised by status alone.
      const detail = error?.message || error?.detail || error?.error || 'Unknown error'
      const kind =
        error?.status === 409 || /already in flight/i.test(detail)
          ? 'alreadyRunning'
          : 'startFailed'

      dispatch(setError({ criteriaScid, error: { kind, detail } }))
      toast.error(
        kind === 'alreadyRunning' ? detail : `Could not start the optimization: ${detail}`,
        { cause: error }
      )

      return null
    } finally {
      dispatch(setLoading({ criteriaScid, loading: { start: false } }))
    }
  }

/**
 * Read a run into its criterion's entry.
 *
 * `criteriaScid` is required rather than optional: it decides WHERE the run is written, so a caller that does not
 * know the criterion has nothing to poll on behalf of.
 */
export const pollOptimization =
  ({ optimizationId, criteriaScid, quiet = false }) =>
  async (dispatch) => {
    if (!quiet) {
      dispatch(setLoading({ criteriaScid, loading: { poll: true } }))
    }

    try {
      const response = await fetchingAPI(
        `${apiService.summary}/promptimizer/optimize/${optimizationId}`,
        'GET',
        dispatch
      )
      const data = await readBody(response)

      dispatch(setRun({ criteriaScid, run: toRun(data) }))

      return data.status
    } catch (error) {
      dispatch(setError({ criteriaScid, error: { kind: 'pollFailed', detail: error?.message } }))

      return null
    } finally {
      if (!quiet) {
        dispatch(setLoading({ criteriaScid, loading: { poll: false } }))
      }
    }
  }

/**
 * The calls a candidate answered differently from the prompt running today, with the reference answer for each.
 *
 * This is the evidence behind "fixes 9, breaks 2" — the part that makes a result checkable rather than a number to
 * be trusted. The winner's calls are fetched as soon as the result lands, because those counts are the headline;
 * any other candidate's wait until its list is opened.
 */
export const fetchChangedCalls =
  ({ optimizationId, candidateId, criteriaScid, scope }) =>
  async (dispatch) => {
    // A scoped fetch (the feedback loop's regression drill-in) is stored under its own key so it
    // never clobbers the same candidate's official list.
    const storageKey = scope ? `${candidateId}:${scope}` : candidateId
    dispatch(setCallsLoading({ candidateId: storageKey, loading: true }))

    try {
      // The Calibration Agent's regression drill-in has its own route, open to customer prompt editors.
      const path =
        scope === REGRESSION_SCOPE
          ? `calibration/runs/${optimizationId}/regression-calls`
          : `promptimizer/optimize/${optimizationId}/calls`
      const response = await fetchingAPI(
        `${apiService.summary}/${path}?candidate_id=${candidateId}`,
        'GET',
        dispatch
      )
      const data = await readBody(response)

      dispatch(setChangedCalls({ candidateId: storageKey, calls: data?.calls ?? [] }))
      dispatch(setCallsError({ candidateId: storageKey, error: null }))

      return data?.calls ?? []
    } catch (error) {
      dispatch(
        setCallsError({
          candidateId: storageKey,
          error: {
            kind: 'callsFailed',
            message: apiErrorMessage(error, 'Could not load the calls that changed answers.'),
          },
        })
      )
      // A scoped list belongs to the Calibration Agent's report — its failure is not the QA Copilot optimizer's.
      if (!scope) {
        dispatch(setError({ criteriaScid, error: { kind: 'callsFailed', detail: error?.message } }))
      }

      return null
    } finally {
      dispatch(setCallsLoading({ candidateId: storageKey, loading: false }))
    }
  }

/**
 * Put a finished result away for good.
 *
 * Recorded server-side rather than in the component: this is also the escape hatch for the staleness we
 * deliberately don't model (changing a criterion's fallback leaves its prompt row untouched, so the run still
 * looks current), and an escape hatch a reload undoes is not one.
 *
 * The id is remembered locally as well, because clearing the entry re-arms recovery and the server write may not
 * have landed yet — see `dismissedIds`. By ID, so the next run on this criterion is unaffected.
 *
 * The entry is cleared regardless of what the server says — a failed write is no reason to leave a result on screen
 * after the user asked for it to go. The cost is that it returns on the next reload.
 */
export const dismissOptimization =
  ({ optimizationId, criteriaScid }) =>
  async (dispatch) => {
    dispatch(recordDismissed({ optimizationId }))
    dispatch(resetRun({ criteriaScid }))

    try {
      await fetchingAPI(
        `${apiService.summary}/promptimizer/optimize/${optimizationId}/dismissed`,
        'POST',
        dispatch
      )

      return true
    } catch (error) {
      return false
    }
  }

export const cancelOptimization =
  ({ optimizationId, criteriaScid }) =>
  async (dispatch) => {
    dispatch(setLoading({ criteriaScid, loading: { cancel: true } }))

    try {
      const response = await fetchingAPI(
        `${apiService.summary}/promptimizer/optimize/${optimizationId}/cancel`,
        'POST',
        dispatch
      )
      const data = await readBody(response)

      dispatch(setRun({ criteriaScid, run: { status: data.status } }))

      // Stopping isn't instant — calls already in flight finish and are still billed. Saying so stops someone
      // clicking twice because nothing appeared to happen.
      if (data.cancelled) {
        toast.success('Stopping. Work already in progress will finish shortly.')
      }

      return data.cancelled
    } catch (error) {
      toast.error('Could not stop the optimization', { cause: error })

      return false
    } finally {
      dispatch(setLoading({ criteriaScid, loading: { cancel: false } }))
    }
  }

/**
 * Put a candidate's text into the criterion form the reviewer already has open.
 *
 * No server-side adopt is needed: typing a prompt into the editor and pressing Save is already how any prompt
 * changes here, so the versioned write happens through the path that already gets it right.
 *
 * Two events, deliberately distinct: APPLIED (here) is intent, ADOPTED (`recordAdoptionAfterSave`) is the save
 * landing. The gap between them measures how often a suggestion doesn't survive a second read.
 *
 * The POST's failure is swallowed — the text is already in the editor, and a reviewer's work must not appear to
 * fail because a telemetry call did.
 */
export const applyCandidatePrompt =
  ({ optimizationId, candidate, criteriaScid, setFieldValue }) =>
  async (dispatch) => {
    if (isNil(candidate) || isEmpty(candidate.prompt_text)) {
      toast.error('That version has no prompt text to use')

      return false
    }

    setFieldValue('user_prompt', candidate.prompt_text)
    toast.success('Prompt updated. Review it and save the scorecard to keep it.')

    dispatch(
      setPendingAdoption({
        criteriaScid,
        pendingAdoption: { optimizationId, candidateId: candidate.candidate_id },
      })
    )

    try {
      await fetchingAPI(
        `${apiService.summary}/promptimizer/optimize/${optimizationId}/applied`,
        'POST',
        dispatch,
        JSON.stringify({ candidate_id: candidate.candidate_id })
      )
    } catch (error) {
      // Deliberately silent — see above.
    }

    return true
  }

/**
 * Undo an apply: put the reviewer's own prompt back in the form.
 *
 * Touches `user_prompt` and nothing else, so the rest of the form survives — the scorecard's own Discard is the
 * blunt alternative. The result panel stays up: changing your mind about a candidate is not a reason to lose the
 * run that produced it.
 *
 * Nothing is sent to the server. `applied` means "took the text", which did happen; adoption simply never fires,
 * and the gap between the two is the signal that pair exists to measure.
 */
export const revertAppliedPrompt =
  ({ originalPrompt, criteriaScid, setFieldValue }) =>
  (dispatch) => {
    if (isEmpty(originalPrompt)) {
      // Only reachable for a run whose starting prompt we cannot recover. Said out loud rather than returning
      // false in silence, which reads as a broken button.
      toast.error("We don't have your original prompt for this run — nothing to put back.")

      return false
    }

    setFieldValue('user_prompt', originalPrompt)
    dispatch(setPendingAdoption({ criteriaScid, pendingAdoption: null }))
    toast.success('Put your original prompt back.')

    return true
  }

/**
 * Record the real adoptions, after a scorecard save succeeded.
 *
 * A no-op unless a candidate was applied, which is what lets the shared scorecard save call this unconditionally
 * without knowing anything about prompt optimization. The evidence the server wants is the prompt version the save
 * created in the criterion's real lineage — and the save response carries it, so nothing here reimplements any
 * part of the scorecard write.
 *
 * Handles several at once — one save can carry a suggestion applied on more than one criterion.
 */
export const recordAdoptionAfterSave = (savedScorecardConfig) => async (dispatch, getState) => {
  const byCriterion = getState().promptimizer?.byCriterion ?? {}
  const pending = Object.entries(byCriterion).filter(([, entry]) => !isNil(entry.pendingAdoption))

  if (isEmpty(pending) || isNil(savedScorecardConfig)) {
    return false
  }

  const savedCriteria = (savedScorecardConfig.sections ?? [])
    .flatMap((section) => section.measures ?? [])
    .flatMap((measure) => measure.criteria ?? [])

  // `allSettled`, so one failed write leaves the others recorded. ⚠ The callback is `async` so that a SYNCHRONOUS
  // throw becomes a rejection rather than escaping — `fetchingAPI` reads the store and may dispatch a logout before
  // it ever returns a promise, and this must never make a successful save look failed.
  const results = await Promise.allSettled(
    pending.map(async ([criteriaScid, entry]) => {
      const newPromptId = savedCriteria.find(
        (criteria) => criteria.scid === criteriaScid
      )?.prompt_id

      if (isNil(newPromptId)) {
        // The criterion was removed before saving, or the response shape surprised us.
        throw new Error(`no saved prompt for criterion ${criteriaScid}`)
      }

      return fetchingAPI(
        `${apiService.summary}/promptimizer/optimize/${entry.pendingAdoption.optimizationId}/adopted`,
        'POST',
        dispatch,
        JSON.stringify({
          candidate_id: entry.pendingAdoption.candidateId,
          new_prompt_id: newPromptId,
        })
      )
    })
  )

  // The whole entry goes once its suggestion is live, not just `pendingAdoption`: left up, the panel would name the
  // version this save replaced and offer text that is already the criterion's. Cleared even where the write failed,
  // rather than left to attach itself to a later, unrelated save.
  pending.forEach(([criteriaScid]) => dispatch(resetRun({ criteriaScid })))

  return results.some((result) => result.status === 'fulfilled')
}
